import { parseDate } from '@internationalized/date';
import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useId } from 'react';
import { type DateValue } from 'react-aria-components';
import { pipe as rPipe, map as rMap, filter as rFilter } from 'remeda';

import { CampaignRequestProgramStageEnum } from '@/libs/openapi';
import {
  Button,
  CALENDAR_SLOTS,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DIALOG_SLOTS,
  DialogBar,
  Form,
  Label,
  ListBox,
  ListBoxItem,
  NumberField,
  Input,
  Popover,
  Select,
  SelectInput,
  Text,
  View,
} from '@/libs/serenity-ui';
import { formatEnumValue } from '@/utils/format.util';

const STAGE_OPTIONS = (Object.values(CampaignRequestProgramStageEnum).filter((v) => typeof v === 'number') as CampaignRequestProgramStageEnum[]).map(
  (stage) => ({ id: stage, label: formatEnumValue(CampaignRequestProgramStageEnum)(stage) }),
);

const META_FIELDS = new Set<string>([]);

interface ProgramFiltersFormValues {
  id: number | undefined;
  stage: CampaignRequestProgramStageEnum | undefined;
  startDateGte: string | undefined;
  endDateLte: string | undefined;
}

interface ProgramFiltersFormProps {
  onClose?: () => void;
}

export function ProgramFiltersForm ({ onClose }: ProgramFiltersFormProps) {
  const id = useId();
  const search = useSearch({ from: '/_main/_list/programs/' });
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      id: undefined,
      stage: undefined,
      startDateGte: undefined,
      endDateLte: undefined,
      ...Object.fromEntries(search.columnFilters.map(({ id: filterId, value }) => [ filterId, value ])),
    } as ProgramFiltersFormValues,
    onSubmit: () => {
      navigate({
        to: '.',
        search: (prev) => ({
          sorting: prev.sorting,
          columnFilters: rPipe(
            Object.entries(form.state.values),
            rFilter(([ key, value ]) => !META_FIELDS.has(key) && value != null && value !== ''),
            rMap(([ filterId, value ]) => ({ id: filterId, value })),
          ),
          pagination: { pageIndex: 0, pageSize: prev.pagination?.pageSize ?? 100 },
        }),
      });
      onClose?.();
    },
  });

  return (
    <>
      <Form
        id={id}
        style={formStyles.base}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}>
        {(renderProps) => (
          <>
            <form.Field name="id">
              {(field) => (
                <View style={renderProps.fieldStyles}>
                  <Text color={renderProps.labelColor} id={`${id}-program-id`} size={16}>ID</Text>
                  <NumberField
                    id={field.name}
                    minValue={1}
                    name={field.name}
                    size={32}
                    value={field.state.value ?? NaN}
                    onBlur={field.handleBlur}
                    onChange={(value) => field.handleChange(Number.isNaN(value) ? undefined : value)}>
                    <Input aria-labelledby={`${id}-program-id`} />
                  </NumberField>
                </View>
              )}
            </form.Field>

            <form.Field name="stage">
              {(field) => (
                <Select
                  id={field.name}
                  name={field.name}
                  style={renderProps.fieldStyles}
                  value={field.state.value ?? null}
                  onChange={(key) => field.handleChange(key != null ? (Number(key) as CampaignRequestProgramStageEnum) : undefined)}>
                  <Label color={renderProps.labelColor} size={16} style={selectLabelStyles.base}>Stage</Label>
                  <SelectInput size={32} />
                  <Popover>
                    <ListBox items={STAGE_OPTIONS}>
                      {(item) => (
                        <ListBoxItem id={item.id} size={40} textValue={item.label}>
                          {item.label}
                        </ListBoxItem>
                      )}
                    </ListBox>
                  </Popover>
                </Select>
              )}
            </form.Field>

            <form.Field name="startDateGte">
              {(field) => (
                <DatePicker
                  granularity="day"
                  id={field.name}
                  style={renderProps.fieldStyles}
                  value={field.state.value ? parseDate(field.state.value) as unknown as DateValue : null}
                  onBlur={field.handleBlur}
                  onChange={(value) => {
                    field.handleChange(value ? value.toString().substring(0, 10) : undefined);
                  }}>
                  <Label color={renderProps.labelColor}>Start date</Label>
                  <DateInput size={32} style={datePickerInputStyles.base} />
                  <Popover style={popoverStyles.base}>
                    <Calendar>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarHeader isSmall />
                      </View>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarGrid weekdayStyle="short">
                          <CalendarGridHeader>
                            {(day) => <CalendarHeaderCell isSmall>{day}</CalendarHeaderCell>}
                          </CalendarGridHeader>
                          <CalendarGridBody>
                            {(date) => <CalendarCell date={date} isSmall />}
                          </CalendarGridBody>
                        </CalendarGrid>
                      </View>
                    </Calendar>
                  </Popover>
                </DatePicker>
              )}
            </form.Field>

            <form.Field name="endDateLte">
              {(field) => (
                <DatePicker
                  granularity="day"
                  id={field.name}
                  style={renderProps.fieldStyles}
                  value={field.state.value ? parseDate(field.state.value) as unknown as DateValue : null}
                  onBlur={field.handleBlur}
                  onChange={(value) => {
                    field.handleChange(value ? value.toString().substring(0, 10) : undefined);
                  }}>
                  <Label color={renderProps.labelColor}>End date</Label>
                  <DateInput size={32} style={datePickerInputStyles.base} />
                  <Popover style={popoverStyles.base}>
                    <Calendar>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarHeader isSmall />
                      </View>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarGrid weekdayStyle="short">
                          <CalendarGridHeader>
                            {(day) => <CalendarHeaderCell isSmall>{day}</CalendarHeaderCell>}
                          </CalendarGridHeader>
                          <CalendarGridBody>
                            {(date) => <CalendarCell date={date} isSmall />}
                          </CalendarGridBody>
                        </CalendarGrid>
                      </View>
                    </Calendar>
                  </Popover>
                </DatePicker>
              )}
            </form.Field>
          </>
        )}
      </Form>

      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <Button
          isRounded
          size={40}
          variant="ghost"
          onPress={() => {
            form.reset();
            navigate({
              to: '.',
              search: (prev) => ({
                sorting: prev.sorting,
                columnFilters: [],
                pagination: { pageIndex: 0, pageSize: prev.pagination?.pageSize ?? 100 },
              }),
            });
          }}>
          Clear all
        </Button>
        <Button form={id} isRounded size={40} type="submit">Apply</Button>
      </DialogBar>
    </>
  );
}

const formStyles = stylex.create({
  base: {
    overflow: 'auto',
    paddingBlock: 8,
    scrollbarWidth: 'none',
  },
});

const selectLabelStyles = stylex.create({
  base: {
    alignSelf: 'center',
  },
});

const datePickerInputStyles = stylex.create({
  base: {
    width: 'calc(100% - 1px)',
  },
});

const popoverStyles = stylex.create({
  base: {
    minWidth: 0,
  },
});
